// The character's brief, read before committing to become them. `/demo/:scenarioId
// /:seatLabel` is reached only from a link on `DemoScenarios.tsx`'s scenario list, and
// looking at it creates nothing — the plan is written only when "Start as this
// character" below is pressed. That is what makes browsing safe: reading three
// characters and choosing one no longer leaves the other two as abandoned demos.
//
// Renders the brief with the same `Markdown` component `CharacterSheet.tsx` (the
// in-plan screen, `/plans/:planId/sheet`) uses, rather than sharing a screen between the
// two: they carry different actions — this one starts and goes back to the list, that
// one only goes back to its plan — and a single component switched between them by a
// boolean would be two screens wearing one shape, not one screen actually shared.
//
// Reuses `DemoScenarios.tsx`'s retry-after-mount mechanism verbatim: a cold load can
// render this route before the sign-in vendor has published a bearer, the first read
// goes out with none and gets a 401, and the session's next token publish must retry the
// same read rather than leaving the screen blank forever.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import { Markdown } from '../markdown.js';
import { session } from '../session.js';

export type CharacterPreviewProps = {
  client: ApiClient;
};

export function CharacterPreview({ client }: CharacterPreviewProps): JSX.Element {
  const { scenarioId, seatLabel } = useParams<{
    scenarioId: string;
    seatLabel: string;
  }>();
  const [text, setText] = useState<string | null>(null);
  // A retryable failure: unreachable, a bad response, anything not named below.
  const [problem, setProblem] = useState<string | null>(null);
  // The scenario or seat named in the address is not one the server recognises — a 404,
  // or an address with no `scenarioId`/`seatLabel` at all. Distinct from `text === null`,
  // which is also true while the read is still in flight.
  const [notFound, setNotFound] = useState(false);
  // The same split `DemoScenarios.tsx` makes: a stale bearer, or — the moment right after
  // a fresh page load — no bearer yet. The vendor's next token publish cures either one.
  const [waiting, setWaiting] = useState(false);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  // A slow first read must not overwrite a fast retry: last started wins. Needed for the
  // same reason `DemoScenarios.tsx` carries one — the retry effect below can call `load`
  // again while an earlier call is still in flight.
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (scenarioId === undefined || seatLabel === undefined) {
      setNotFound(true);
      return;
    }
    const mine = (generation.current += 1);
    try {
      const sheet = await client.readDemoSheet(scenarioId, seatLabel);
      if (mine !== generation.current) {
        return;
      }
      setText(sheet);
      setProblem(null);
      setNotFound(false);
      setWaiting(false);
    } catch (error) {
      if (mine !== generation.current) {
        return;
      }
      if (error instanceof ApiFailure && error.code === 'unreachable') {
        setProblem('Couldn’t reach the server. Try again in a moment.');
        setWaiting(false);
      } else if (error instanceof ApiFailure && error.status === 401) {
        setProblem(null);
        setWaiting(true);
      } else if (error instanceof ApiFailure && error.status === 404) {
        setProblem(null);
        setNotFound(true);
        setWaiting(false);
      } else {
        // Everything else: a 500, a response that would not decode, anything unnamed
        // above. Never reads as "no such character" — that would misreport a request
        // that failed as one that was actually answered.
        setProblem('That didn’t load. Try again.');
        setWaiting(false);
      }
    }
  }, [client, scenarioId, seatLabel]);

  useEffect(() => {
    void load();
  }, [load]);

  // A fresh bearer and a returning network re-read the sheet — but only while it has
  // never rendered, so the vendor's periodic token republish does not become a refetch
  // schedule. This is what turns a cold page load's first, tokenless request (a 401) into
  // the brief actually appearing a moment later, with nobody reloading.
  useEffect(() => {
    const retry = (): void => {
      if (text === null) {
        void load();
      }
    };
    const unsubscribe = session.subscribe(retry);
    window.addEventListener('online', retry);
    return () => {
      unsubscribe();
      window.removeEventListener('online', retry);
    };
  }, [load, text]);

  const start = async (): Promise<void> => {
    if (scenarioId === undefined || seatLabel === undefined || starting) {
      return;
    }
    setStarting(true);
    try {
      const started = await client.startDemo(scenarioId, seatLabel);
      // The plan itself, not its character-sheet screen: a visitor arriving here has
      // just read the brief, so there is nothing left for that screen to tell them.
      navigate(`/plans/${started.planId}`);
    } catch {
      setProblem('That demo didn’t start. Try again.');
      setStarting(false);
    }
  };

  return (
    <main className="screen screen--sheet">
      <div className="sheetHead">
        <h1 className="screen__title">Your character</h1>
        <Link className="button button--outline" to="/demo">
          Back to the list
        </Link>
      </div>
      {waiting ? (
        <p className="screen__loading" role="status">
          Signing you back in…
        </p>
      ) : null}
      {problem === null ? null : (
        <p className="screen__problem" role="status">
          {problem}
        </p>
      )}
      {notFound ? (
        <p className="screen__empty">There’s no character here.</p>
      ) : text === null ? null : (
        <>
          <Markdown className="sheet" text={text} />
          <button
            type="button"
            className="button button--primary"
            disabled={starting}
            onClick={() => void start()}
          >
            {starting ? 'Starting…' : 'Start as this character'}
          </button>
        </>
      )}
    </main>
  );
}
